import React, {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react'
import { View, StyleSheet } from 'react-native'
import {
  Avatar,
  List,
  Menu,
  Chip,
  IconButton,
  Button,
} from 'react-native-paper'
import PaginatedFlatList from '../../../../../@ui/containers/lists/paginated-flat.list'
import {
  ClusterMember,
  useGetMembersByClusterIdQuery,
  useUpdateClusterMemberMutation,
} from '../../../../../generated/graphql'
import { useTranslation } from 'react-i18next'
import { useIsFocused, useNavigation } from '@react-navigation/native'
import { useTheme } from '../../../../../hooks/theme.hook'
import { useAuthContext } from '../../../../../contexts/auth-context'
import { MemberActionsModal } from './member-actions.modal'
import { MelddSearchBar } from '../../../../../@ui/form/meldd-search.input'
import { AppNavigation } from '../../../../../navigation/types'

interface ClusterMemberListProps {
  cluster: ClusterByIdQuery['clusterById']
  onUpdate: () => void
  isOwner?: boolean
  onSwitchMode: () => void
}

export interface ClusterMemberListRef {
  refetch: () => void
}

const RoleDropdown = ({
  member,
  onUpdate,
  isOwner,
}: {
  member: ClusterMember
  onUpdate: () => void
  isOwner?: boolean
}) => {
  const { userId: currentUserId } = useAuthContext()
  const { t } = useTranslation('clusters')
  const theme = useTheme()
  const [doUpdateMember] = useUpdateClusterMemberMutation()
  const [openMenuId, setOpenMenuId] = useState<string | null>(null)

  async function changeRole(id: string, isModerator: boolean) {
    try {
      await doUpdateMember({
        variables: {
          input: { id, isModerator },
        },
      })
      onUpdate()
    } catch (error) {
      console.error('Error updating member role:', error)
    }
  }

  if (isOwner) {
    return (
      <Chip
        mode="flat"
        style={{
          backgroundColor: theme.colors.primaryContainer,
        }}
      >
        {t('owner')}
      </Chip>
    )
  }

  const isMySelf = currentUserId === member.userId
  return (
    <>
      <Menu
        visible={openMenuId === member.id}
        onDismiss={() => setOpenMenuId(null)}
        anchor={
          <Chip
            mode="flat"
            onPress={() => !isMySelf && setOpenMenuId(member.id)}
            style={{
              backgroundColor: member.isModerator
                ? theme.colors.tertiaryContainer
                : theme.colors.surfaceVariant,
            }}
          >
            {member.isModerator ? t('Moderator') : t('member')}
          </Chip>
        }
      >
        <Menu.Item
          onPress={() => {
            changeRole(member.id, true)
            setOpenMenuId(null)
          }}
          title={t('Moderator')}
          leadingIcon={member.isModerator ? 'check' : undefined}
        />
        <Menu.Item
          onPress={() => {
            changeRole(member.id, false)
            setOpenMenuId(null)
          }}
          title={t('member')}
          leadingIcon={!member.isModerator ? 'check' : undefined}
        />
      </Menu>
    </>
  )
}

export const ClusterMemberList = forwardRef<
  ClusterMemberListRef,
  ClusterMemberListProps
>(({ cluster, onUpdate, isOwner = false, onSwitchMode }, ref) => {
  const { t } = useTranslation('clusters')
  const theme = useTheme()
  const navigation = useNavigation<AppNavigation>()
  const [modalVisible, setModalVisible] = useState(false)
  const [selectedMember, setSelectedMember] = useState<any>(null)
  const [search, setSearch] = useState<string>('')

  const { data, refetch } = useGetMembersByClusterIdQuery({
    variables: {
      clusterId: cluster.id,
    },
  })

  const isFocused = useIsFocused()

  React.useEffect(() => {
    if (isFocused) {
      refetch()
    }
  }, [isFocused, refetch])

  useImperativeHandle(
    ref,
    () => ({
      refetch: () => refetch(),
    }),
    [refetch]
  )

  async function handleActionSuccess() {
    await refetch()
    onUpdate()
    setModalVisible(false)
  }

  function handleTransferSuccess() {
    setModalVisible(false)
    // Navigate back or refresh
    navigation.navigate('ClusterCards', {
      clusterId: cluster.id,
      nodeId: cluster.nodeId,
    })
  }

  function showActionsModal(member: any) {
    setSelectedMember(member)
    setModalVisible(true)
  }

  // Filter members based on search
  const filteredMembers = React.useMemo(() => {
    const members = data?.clusterMembersByClusterId || []
    if (!search) return members
    return members.filter((member) =>
      member.user.username.toLowerCase().includes(search.toLowerCase())
    )
  }, [data?.clusterMembersByClusterId, search])

  const styles = useMemo(
    () =>
      StyleSheet.create({
        searchContainer: {
          marginBottom: theme.spacing(2),
          flexDirection: 'row',
          gap: theme.spacing(2),
          alignItems: 'center',
        },
      }),
    [theme]
  )

  return (
    <View>
      <View style={styles.searchContainer}>
        <View style={{ flex: 1 }}>
          <MelddSearchBar
            value={search}
            onChangeText={setSearch}
            placeholder={t('filter-members')}
          />
        </View>
        <Button mode="outlined" icon="account-plus" onPress={onSwitchMode}>
          {t('add')}
        </Button>
      </View>
      <PaginatedFlatList
        data={filteredMembers as ClusterMember[]}
        renderItem={({ item }) => {
          const member = item as ClusterMember

          const isMemberOwner =
            cluster?.ownerId === (item as ClusterMember).userId
          return (
            <View>
              <List.Item
                title={member.user.username}
                left={() => (
                  <Avatar.Text size={32} label={member.user.username[0]} />
                )}
                right={() => (
                  <View
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                    <RoleDropdown
                      member={member}
                      onUpdate={handleActionSuccess}
                      isOwner={isMemberOwner}
                    />
                    {!isMemberOwner && isOwner && (
                      <IconButton
                        icon="dots-vertical"
                        onPress={() => showActionsModal(member)}
                        accessibilityLabel={t('member-actions')}
                      />
                    )}
                  </View>
                )}
              />
            </View>
          )
        }}
        onLoadMore={async () => {
          // No pagination for cluster members yet
        }}
        total={filteredMembers.length}
        onRefresh={async () => {
          await refetch()
        }}
      />
      {selectedMember && (
        <MemberActionsModal
          visible={modalVisible}
          onClose={() => setModalVisible(false)}
          clusterId={cluster.id}
          member={selectedMember}
          onTransferSuccess={handleTransferSuccess}
          onDeleteSuccess={handleActionSuccess}
        />
      )}
    </View>
  )
})

const styles = StyleSheet.create({})
